import { BarChart } from '@mantine/charts';
import {
  Box,
  Flex,
  Group,
  Text,
  Title,
  Paper,
  ScrollArea,
  Center,
  SegmentedControl,
  Loader,
  Stack,
  ActionIcon,
} from '@mantine/core';
import { callStatusColors } from 'constants/reporting';
import { CallStatus } from 'interfaces/Reporting';
import { useEffect, useState, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { useContext } from 'react';
import ReportContext from 'context/ReportContext';
import { DateRange } from 'components/DateRangeFilter/Dropdown';
import {
  IconChartBarOff,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';

import classes from './CallStatusChart.module.scss';
import { useDeviceType } from 'hooks/useDeviceType';
import useCallsQuery from 'hooks/useCallsQuery';

type Resolution = 'hours' | 'days' | 'months';
type ChartData = { [key: string]: number | string | undefined };

enum ResolutionOptions {
  HOURS = 'hours',
  DAYS = 'days',
  MONTHS = 'months',
}

enum CallStatusOptions {
  RESOLVED = 'Resolved calls',
  TRANSFERRED = 'Transferred calls',
  MESSAGE_TAKEN = 'Message taken',
  UNRESOLVED = 'Unresolved',
  INCOMPLETE = 'Incomplete',
  NO_CALLS = 'No calls',
  UNKNOWN = 'Unknown',
}

interface ChartDataItem extends ChartData {
  date: string;
  [CallStatusOptions.RESOLVED]: number;
  [CallStatusOptions.TRANSFERRED]: number;
  [CallStatusOptions.MESSAGE_TAKEN]: number;
  [CallStatusOptions.UNRESOLVED]: number;
  [CallStatusOptions.INCOMPLETE]: number;
  [CallStatusOptions.UNKNOWN]: number;
  [CallStatusOptions.NO_CALLS]?: number;
}

const series = [
  {
    name: CallStatusOptions.RESOLVED,
    color: callStatusColors[CallStatus.RESOLVED],
  },
  {
    name: CallStatusOptions.TRANSFERRED,
    color: callStatusColors[CallStatus.TRANSFERRED],
  },
  {
    name: CallStatusOptions.MESSAGE_TAKEN,
    color: callStatusColors[CallStatus.MESSAGE_TAKEN],
  },
  {
    name: CallStatusOptions.UNRESOLVED,
    color: callStatusColors[CallStatus.UNRESOLVED],
  },
  {
    name: CallStatusOptions.INCOMPLETE,
    color: callStatusColors[CallStatus.INCOMPLETE],
  },
  {
    name: CallStatusOptions.UNKNOWN,
    color: callStatusColors[CallStatus.UNKNOWN],
  },
  {
    name: CallStatusOptions.NO_CALLS,
    color: 'url(#zero-pattern)',
  },
];

const CallStatusChart = () => {
  const {
    data: { dateRange },
  } = useContext(ReportContext);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const [resolution, setResolution] = useState<Resolution>('days');
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const { isDesktop } = useDeviceType();
  const [chartDateRange, setChartDateRange] = useState<DateRange | undefined>(
    dateRange
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useCallsQuery({ dateRange });

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const getResolutionOptions = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) return 'days';

    const diffHours = dayjs(range.to).diff(range.from, 'hour');
    const diffDays = dayjs(range.to).diff(range.from, 'day');
    const diffMonths = dayjs(range.to).diff(range.from, 'month');

    if (diffHours <= 48) return 'hours';
    if (diffDays >= 2 && diffDays <= 60) return 'days';
    if (diffMonths >= 2 && diffMonths <= 12) return 'months';

    return 'days';
  };

  const allCalls = useMemo(() => {
    return data?.pages.flatMap((page: any) => page.items) || [];
  }, [data]);

  useEffect(() => {
    if (
      dateRange?.from !== chartDateRange?.from ||
      dateRange?.to !== chartDateRange?.to
    ) {
      setChartDateRange(dateRange);
      setResolution(getResolutionOptions(dateRange));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, allCalls]);

  // Generate all time periods between start and end date
  const generateTimePeriods = (startDate: Date, endDate: Date) => {
    const periods: string[] = [];
    let currentDate = dayjs(startDate);
    const end = dayjs(endDate);

    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      let timeKey: string;

      switch (resolution) {
        case ResolutionOptions.HOURS:
          timeKey = currentDate.format('MMM D, HH:00');
          currentDate = currentDate.add(1, 'hour');
          break;
        case ResolutionOptions.DAYS:
          timeKey = currentDate.format('MMM D');
          currentDate = currentDate.add(1, 'day');
          break;
        case ResolutionOptions.MONTHS:
          timeKey = currentDate.format('MMM YYYY');
          currentDate = currentDate.add(1, 'month');
          break;
        default:
          timeKey = currentDate.format('MMM D');
          currentDate = currentDate.add(1, 'day');
      }

      periods.push(timeKey);
    }

    return periods;
  };

  useEffect(() => {
    if (!allCalls.length && !dateRange?.from && !dateRange?.to) return;

    const data: { [key: string]: ChartDataItem } = {};

    // Generate all time periods between start and end date
    const startDate = dateRange?.from || dayjs().subtract(24, 'hours').toDate();
    const endDate = dateRange?.to || new Date();
    const allTimePeriods = generateTimePeriods(startDate, endDate);

    // Initialize all time periods with zero values
    allTimePeriods.forEach((timeKey) => {
      data[timeKey] = {
        date: timeKey,
        [CallStatusOptions.RESOLVED]: 0,
        [CallStatusOptions.TRANSFERRED]: 0,
        [CallStatusOptions.MESSAGE_TAKEN]: 0,
        [CallStatusOptions.UNRESOLVED]: 0,
        [CallStatusOptions.INCOMPLETE]: 0,
        [CallStatusOptions.UNKNOWN]: 0,
      };
    });

    // Add call data to the appropriate time periods
    allCalls.forEach((call) => {
      let timeKey: string;
      const date = dayjs(call.start_time);

      switch (resolution) {
        case ResolutionOptions.HOURS:
          timeKey = date.format('MMM D, HH:00');
          break;
        case ResolutionOptions.DAYS:
          timeKey = date.format('MMM D');
          break;
        case ResolutionOptions.MONTHS:
          timeKey = date.format('MMM YYYY');
          break;
        default:
          timeKey = date.format('MMM D');
      }

      if (!data[timeKey]) {
        data[timeKey] = {
          date: timeKey,
          [CallStatusOptions.RESOLVED]: 0,
          [CallStatusOptions.TRANSFERRED]: 0,
          [CallStatusOptions.MESSAGE_TAKEN]: 0,
          [CallStatusOptions.UNRESOLVED]: 0,
          [CallStatusOptions.INCOMPLETE]: 0,
          [CallStatusOptions.UNKNOWN]: 0,
        };
      }

      switch (call.resolution) {
        case CallStatus.RESOLVED:
          data[timeKey][CallStatusOptions.RESOLVED]++;
          break;
        case CallStatus.TRANSFERRED:
          data[timeKey][CallStatusOptions.TRANSFERRED]++;
          break;
        case CallStatus.MESSAGE_TAKEN:
          data[timeKey][CallStatusOptions.MESSAGE_TAKEN]++;
          break;
        case CallStatus.UNRESOLVED:
          data[timeKey][CallStatusOptions.UNRESOLVED]++;
          break;
        case CallStatus.INCOMPLETE:
          data[timeKey][CallStatusOptions.INCOMPLETE]++;
          break;
        case CallStatus.UNKNOWN:
          data[timeKey][CallStatusOptions.UNKNOWN]++;
          break;
      }
    });

    const sortedData = Object.values(data).sort((a, b) =>
      dayjs(a.date, resolution === 'months' ? 'MMM YYYY' : 'MMM D HH:mm').diff(
        dayjs(b.date, resolution === 'months' ? 'MMM YYYY' : 'MMM D HH:mm')
      )
    );

    const truncatedData = truncateConsecutiveZeros(sortedData);

    setChartData(truncatedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCalls, resolution, dateRange]);

  /**
   * 🔧 TECHNICAL DEBT: Chart Data Compression Algorithm
   * 
   * This function compresses consecutive zero-value data points to improve chart performance
   * and visual clarity, but implements a complex algorithm that's difficult to maintain.
   * 
   * PERFORMANCE PROBLEM:
   * - Large date ranges (e.g., 1 year) create thousands of data points
   * - Most data points are zeros (no calls on specific days/hours)
   * - Recharts library slows down significantly with 1000+ data points
   * - Mobile devices struggle with large chart datasets
   * 
   * CURRENT ALGORITHM COMPLEXITY:
   * - Loops through all data points to identify zero sequences
   * - Complex state tracking (zeroCount, startIndex, endIndex)
   * - String concatenation for date range labels ("Jan 1 — Jan 7")
   * - Special handling for single vs multiple consecutive zeros
   * - Uses Infinity as sentinel value for "no calls" visualization
   * 
   * MAINTENANCE ISSUES:
   * - Algorithm is difficult to understand and modify
   * - No unit tests for edge cases (single day ranges, timezone boundaries)
   * - Hardcoded logic for different chart resolutions (hours/days/months)
   * - Date formatting inconsistencies across different locales
   * 
   * REFACTORING NEEDED:
   * 1. Replace with proper data aggregation at API level
   * 2. Implement server-side data compression for large ranges
   * 3. Use chart library's built-in data sampling features
   * 4. Add comprehensive unit tests for compression algorithm
   * 5. Consider virtual scrolling for very large datasets
   * 
   * ALTERNATIVE APPROACHES:
   * - Use recharts' dataKey filtering for sparse data
   * - Implement lazy loading with data windowing
   * - Pre-aggregate data at different resolution levels
   * - Use canvas-based charting library for better performance
   * 
   * TIMELINE: Refactor during reporting system performance optimization (Q2 2024)
   */
  // Function to truncate consecutive zero values
  const truncateConsecutiveZeros = (data: ChartDataItem[]): ChartDataItem[] => {
    if (data.length <= 3) return data;

    const result: ChartDataItem[] = [];
    let zeroCount = 0;
    let zeroCountStartIndex = 0;
    let zeroCountEndIndex = 0;

    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const hasCalls = Object.values(CallStatusOptions).some(
        (key) => !!item?.[key]
      );

      if (zeroCount >= 1 && (hasCalls || i === data.length - 1)) {
        result.push({
          date:
            zeroCount === 1
              ? data[zeroCountStartIndex].date
              : `${data[zeroCountStartIndex].date} — ${data[zeroCountEndIndex].date}`,
          [CallStatusOptions.RESOLVED]: 0,
          [CallStatusOptions.TRANSFERRED]: 0,
          [CallStatusOptions.MESSAGE_TAKEN]: 0,
          [CallStatusOptions.UNRESOLVED]: 0,
          [CallStatusOptions.INCOMPLETE]: 0,
          [CallStatusOptions.UNKNOWN]: 0,
          [CallStatusOptions.NO_CALLS]: Infinity,
        });
      }

      if (hasCalls) {
        result.push(item);
        zeroCount = 0;
        zeroCountStartIndex = 0;
        zeroCountEndIndex = 0;
      } else {
        zeroCount++;

        if (zeroCount === 1) {
          zeroCountStartIndex = i;
        }

        if (zeroCount >= 1) {
          zeroCountEndIndex = i;
        }

        if (i === data.length - 1) {
          zeroCount = 0;
        }
      }
    }

    return result;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Process chart data to hide "No calls" values in tooltips
  const processedChartData = useMemo(() => {
    if (!chartData.length) return [];

    return chartData.map((item) => {
      const processedItem = { ...item };

      if (processedItem[CallStatusOptions.NO_CALLS]) {
        return {
          date: item.date,
          [CallStatusOptions.NO_CALLS]: item[CallStatusOptions.NO_CALLS],
        };
      }

      if (!processedItem[CallStatusOptions.UNKNOWN]) {
        const { [CallStatusOptions.UNKNOWN]: _, ...rest } = processedItem;
        return {
          ...rest,
        };
      }

      return processedItem;
    });
  }, [chartData]);

  const handleScroll = () => {
    if (scrollAreaRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollAreaRef.current;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  const scrollLeft = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollBy({
        left: -1 * scrollAreaRef.current.clientWidth,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollBy({
        left: scrollAreaRef.current.clientWidth,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;

    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (scrollArea) {
        scrollArea.removeEventListener('scroll', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollAreaRef.current]);

  return (
    <Paper
      ref={loadMoreRef}
      withBorder
      p={{ base: 'md', lg: 'lg' }}
      radius="md"
      h="100%"
    >
      <Flex direction="column" justify="space-between">
        <Group justify="space-between" mb="xl" gap="xs">
          <Title size="1.5rem" className={classes.title}>
            Call status trends {isDesktop ? 'over time' : ''}
          </Title>
          <SegmentedControl
            size="xs"
            disabled={isLoading || allCalls.length === 0}
            withItemsBorders={false}
            value={resolution}
            styles={{
              label: {
                fontSize: '0.8rem',
                padding: '0.1875rem 0.5rem',
              },
            }}
            onChange={(value: string) => {
              // Reset chart data before changing resolution to prevent glitch
              setChartData([]);
              setResolution(value as Resolution);
            }}
            data={Object.values(ResolutionOptions).map((opt) => ({
              label: opt.charAt(0).toUpperCase() + opt.slice(1),
              value: opt,
            }))}
          />
        </Group>

        {isLoading || hasNextPage || isFetchingNextPage ? (
          <Center h={330}>
            <Stack align="center">
              <Loader
                size="md"
                type="dots"
                color="var(--mantine-color-gray-3)"
              />
            </Stack>
          </Center>
        ) : chartData.length === 0 ? (
          <Center h={330}>
            <Flex direction="column" align="center" gap="md">
              <IconChartBarOff
                size={60}
                color="var(--mantine-color-dimmed)"
                opacity={0.3}
                stroke={0.5}
              />
              <Text c="dimmed" size="sm">
                No call data available for the selected date range
              </Text>
            </Flex>
          </Center>
        ) : (
          <Flex direction="column" gap="md">
            <Flex align="center" justify="space-between" pos="relative">
              {!isAtStart && (
                <ActionIcon
                  onClick={scrollLeft}
                  size="lg"
                  pos="absolute"
                  left={0}
                  style={{ zIndex: 1, backgroundColor: 'rgba(0,0,0,0.3)' }}
                >
                  <IconChevronLeft stroke={1} />
                </ActionIcon>
              )}
              <ScrollArea.Autosize ref={scrollAreaRef} miw="100%">
                <Box miw={chartData.length * (isDesktop ? 50 : 40)}>
                  <svg width="0" height="0" style={{ position: 'absolute' }}>
                    <defs>
                      <pattern
                        id="zero-pattern"
                        x="0"
                        y="0"
                        width="10"
                        height="10"
                        patternUnits="userSpaceOnUse"
                      >
                        <line
                          x1="0"
                          y1="0"
                          x2="10"
                          y2="10"
                          stroke="rgba(0,0,0,0.1)"
                          strokeWidth="1"
                        />
                        <line
                          x1="10"
                          y1="0"
                          x2="0"
                          y2="10"
                          stroke="rgba(0,0,0,0.1)"
                          strokeWidth="1"
                        />
                      </pattern>
                    </defs>
                  </svg>

                  <BarChart
                    h={{ base: 300, md: 280, lg: 310, xl: 330 }}
                    mb="xs"
                    data={processedChartData}
                    dataKey="date"
                    series={series.map((s) => ({
                      name: s.name,
                      color: s.color,
                    }))}
                    tickLine="y"
                    gridAxis="xy"
                    type="percent"
                    maxBarWidth={60}
                    tooltipAnimationDuration={200}
                    valueFormatter={(value) => {
                      return value === Infinity ? '' : value.toString();
                    }}
                  />
                </Box>
              </ScrollArea.Autosize>
              {!isAtEnd && (
                <ActionIcon
                  onClick={scrollRight}
                  size="lg"
                  pos="absolute"
                  right={0}
                  style={{ zIndex: 1, backgroundColor: 'rgba(0,0,0,0.3)' }}
                >
                  <IconChevronRight stroke={1} />
                </ActionIcon>
              )}
            </Flex>
            <Flex
              align="center"
              rowGap="0.75rem"
              columnGap="xl"
              px={{ base: '1rem', xl: '2rem' }}
              wrap="wrap"
            >
              {series.map(({ name, color }) => (
                <Group key={name} gap="0.75rem">
                  <Box
                    w={12}
                    h={12}
                    style={{
                      border: `2px solid ${
                        name === CallStatusOptions.NO_CALLS
                          ? 'rgba(0,0,0,0.075)'
                          : color
                      }`,
                    }}
                  />

                  <Text size="xs" c="dimmed">
                    {name}
                  </Text>
                </Group>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Paper>
  );
};

export default CallStatusChart;
